/* eslint-disable react-refresh/only-export-components */
import React, { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Switch, useLocation } from 'react-router-dom';
import { Loader } from '@els/els-react--loader';
import Page from '@Containers/Page';
import ConnectedRoute from '@AppRoutes/ConnectedRoute';
import { paths } from '@AppRoutes/constants.js';
import withReducer from '@Hooks/withReducer';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import { setPageTitle } from '@Services/PageTitle';
import ConditionsBreadcrumbs from './components/ConditionsBreadcrumbs';
import ConditionsNavigatorContainer from './ConditionsNavigatorContainer';
import ConditionsDetailContainer from './ConditionsDetailContainer';
import ConditionsDraw from './ConditionsDraw';
import { conditionsReducerKey, CONDITIONS_PAGE_TITLE } from './constants';
import conditionsReducer from './reducer';
import './Conditions.scss';

function getProjectNameFromLocation(locationState = {}) {
  return locationState?.projectName || locationState?.referenceLabel || '';
}

function Conditions() {
  const location = useLocation();
  const [projectName, setProjectName] = useState(() => getProjectNameFromLocation(location.state));
  const [blockingNavigationCount, setBlockingNavigationCount] = useState(0);
  const isBlockingNavigation = blockingNavigationCount > 0;

  const handleProjectLoaded = useCallback((loadedProjectName) => {
    setProjectName(loadedProjectName);
    setPageTitle({ pageName: CONDITIONS_PAGE_TITLE, title: loadedProjectName });
  }, []);

  const setBlockingNavigation = useCallback((isBusy) => {
    const updateBlockingNavigationCount = () => {
      setBlockingNavigationCount((count) => (isBusy ? count + 1 : Math.max(0, count - 1)));
    };

    if (isBusy) {
      flushSync(updateBlockingNavigationCount);
      return;
    }

    updateBlockingNavigationCount();
  }, []);

  useEffect(() => {
    // On detail routes the loaded project owns the breadcrumb label via onProjectLoaded — including
    // when the project is served from the Redux cache. Re-seeding from navigation here would clobber
    // that label on cached revisits, because the child's onProjectLoaded effect runs before this
    // parent effect in the same commit. So only seed/clear the label for the list and draw routes.
    const isDetailRoute = location.pathname !== paths.CONDITIONS && location.pathname !== paths.CONDITIONS_DRAW;
    if (isDetailRoute) return;
    setProjectName(getProjectNameFromLocation(location.state));
  }, [location.pathname, location.state]);

  // ConnectedRoute updates the tracking path name in the store on each navigation;
  // fire the page-view tracking afterwards so every Conditions route (list/draw/detail)
  // emits its configured page name, including intra-feature navigation.
  useEffect(() => {
    RouteChangeTracking.trackOnRouteChangeSuccess();
  }, [location.pathname]);

  return (
    <Page withFooter={false} pageClassName="conditions-root">
      <ConditionsBreadcrumbs projectName={projectName} />
      <Loader isVisible={isBlockingNavigation} isBlocking />
      <Switch>
        <ConnectedRoute exact path={paths.CONDITIONS_DRAW} component={ConditionsDraw} />
        <ConnectedRoute
          exact
          path={paths.CONDITIONS}
          render={() => <ConditionsNavigatorContainer setBlockingNavigation={setBlockingNavigation} />}
        />
        <ConnectedRoute
          path={`${paths.CONDITIONS}/:id`}
          render={(props) => (
            <ConditionsDetailContainer {...props} onProjectLoaded={handleProjectLoaded} setBlockingNavigation={setBlockingNavigation} />
          )}
        />
      </Switch>
    </Page>
  );
}

export default withReducer(conditionsReducerKey, conditionsReducer, true)(Conditions);
